import { NavLink, useLocation } from 'react-router-dom';
import { Badge } from './StatusPill.js';
import { ScrollStrip } from './ScrollStrip.js';
import { formatBadgeCount } from './format.js';

export interface SectionStripItem {
  label: string;
  to: string;
  /** Match the path exactly rather than by prefix. */
  end?: boolean;
  count?: number;
}

export interface SectionStripProps {
  /** Accessible name of the navigation region, e.g. "Administration sections". */
  label: string;
  /** Only the sections the reader holds; never enumerate what they could not reach. */
  items: readonly SectionStripItem[];
  className?: string;
}

/**
 * Module section navigation (02-patterns §2.4): links carrying aria-current, not a tablist, in a
 * scrolling strip with edge indication. An empty strip still draws the rule the page head relies on.
 */
export function SectionStrip({ label, items, className }: SectionStripProps) {
  const location = useLocation();
  if (items.length === 0) {
    return <div className={['section-strip', 'section-strip--empty', className].filter(Boolean).join(' ')} aria-hidden="true" />;
  }
  return (
    <nav className={['section-strip', className].filter(Boolean).join(' ')} aria-label={label}>
      <ScrollStrip selectedKey={location.pathname}>
        <ul className="section-strip__list" role="list">
          {items.map((item) => (
            <li key={item.to} className="section-strip__item">
              <NavLink to={item.to} end={item.end} className="section-strip__link">
                <span className="section-strip__label">{item.label}</span>
                {item.count !== undefined ? <Badge label={`${item.count} in ${item.label}`}>{formatBadgeCount(item.count)}</Badge> : null}
              </NavLink>
            </li>
          ))}
        </ul>
      </ScrollStrip>
    </nav>
  );
}
